import type { CoreData, CountryId, NameEntry, SexIdx } from "./types";
import { TAG } from "./types";

export interface Collection {
  id: string;
  title: string;
  blurb: string | ((core: CoreData) => string);
  /** only offered for these countries (default: all) */
  countries?: CountryId[];
  /** is this name in the collection (for the deck the user is browsing)? */
  test(e: NameEntry, si: SexIdx, core: CoreData): boolean;
  /** how Explore orders the preview (lower first) */
  order(e: NameEntry, si: SexIdx): number;
}

const rankOrInf = (r: number) => (r > 0 ? r : 1e9);

export const COLLECTIONS: Collection[] = [
  {
    id: "top",
    title: "Top 100",
    blurb: (core) => `The most-registered names in ${core.country.name} in ${core.latest}.`,
    test: (e, si) => e.r[si] > 0 && e.r[si] <= 100,
    order: (e, si) => e.r[si],
  },
  {
    id: "rising",
    title: "Doubled in five years",
    blurb: "Given to at least twice as many babies as five years ago.",
    test: (e, si) => (e.t[si] ?? 0) >= 100 && e.c[si] >= 30,
    order: (e, si) => -(e.c[si] * Math.min(e.t[si] ?? 0, 400)),
  },
  {
    id: "vintage",
    title: "Back from the 1900s",
    blurb: "Top 100 in the early 1900s, out of fashion by the 1960s, and back in favour now.",
    test: (e) => !!(e.tags & TAG.VINTAGE),
    order: (e, si) => rankOrInf(e.r[si]),
  },
  {
    id: "comeback",
    title: "Due a comeback",
    blurb: "Top 100 in the early 1900s, and rare today.",
    test: (e, si) => !!(e.tags & TAG.EDWARDIAN) && !(e.tags & TAG.VINTAGE) && (!e.r[si] || e.r[si] > 400),
    order: (e, si) => -(e.c[si] + (e.t[si] ?? 0)),
  },
  {
    id: "timeless",
    title: "Never out of fashion",
    blurb: "In the top 100 for most of the past century, or most of the past 30 years.",
    test: (e) => !!(e.tags & (TAG.TIMELESS | TAG.CLASSIC)),
    order: (e, si) => rankOrInf(e.r[si]),
  },
  {
    id: "new",
    title: "New in five years",
    blurb: "Names that were barely recorded five years ago.",
    test: (e) => !!(e.tags & TAG.NEW),
    order: (e, si) => -e.c[si],
  },
  {
    id: "gems",
    title: "Climbing quietly",
    blurb: "Rising steadily, but still outside the top 300.",
    test: (e, si) => e.r[si] > 300 && e.r[si] <= 2000 && (e.t[si] ?? 0) >= 25,
    order: (e, si) => e.r[si],
  },
  {
    id: "unisex",
    title: "For boys and girls",
    blurb: "Given to both in real numbers.",
    test: (e) => !!(e.tags & TAG.UNISEX),
    order: (e) => -(e.c[0] + e.c[1]),
  },
  {
    id: "short",
    title: "Four letters or fewer",
    blurb: "Short names from the top 1,000.",
    test: (e, si) => e.len <= 4 && e.r[si] > 0 && e.r[si] <= 1000,
    order: (e, si) => e.r[si],
  },
  {
    id: "scotland",
    title: "More popular in Scotland",
    countries: ["uk"],
    blurb: "At least three times as popular in Scotland as in England and Wales.",
    test: (e) => !!(e.tags & TAG.SCOTLAND),
    order: (e, si) => rankOrInf(e.r[si]),
  },
  {
    id: "ni",
    title: "More popular in Northern Ireland",
    countries: ["uk"],
    blurb: "At least three times as popular in Northern Ireland as in England and Wales.",
    test: (e) => !!(e.tags & TAG.NI),
    order: (e, si) => rankOrInf(e.r[si]),
  },
  {
    id: "fading",
    title: "Halved in five years",
    blurb: "Given to fewer than half as many babies as five years ago.",
    test: (e, si) => (e.t[si] ?? 0) <= -50 && e.c[si] >= 20,
    order: (e, si) => e.r[si] || 1e9,
  },
];

/** The collections that make sense for this country's data. */
export function collectionsFor(core: CoreData): Collection[] {
  const cc = core.country.id;
  return COLLECTIONS.filter((c) => !c.countries || c.countries.includes(cc));
}

export function blurbOf(c: Collection, core: CoreData): string {
  return typeof c.blurb === "function" ? c.blurb(core) : c.blurb;
}

export function collectionById(id: string | null): Collection | undefined {
  return id ? COLLECTIONS.find((c) => c.id === id) : undefined;
}
